import { ChevronDown } from "../../../components/Icons/ChevronDown";
import { ChevronUp } from "../../../components/Icons/ChevronUp";
import { WarningIcon } from "../../../components/Icons/WarningIcon";
import { FormText } from "../../../components/Input/FormText";
import { formatAmount } from "../../../utils/helperFunctions";
import {
  getAccountNumberError,
  getPaymentReferenceError,
  sourceLabel,
  typeLabel
} from "./helpers";
import {
  Badge,
  BadgeCheck,
  Card,
  CardAmount,
  CardBody,
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
  CardSubtitle,
  CardTitle,
  ChevronButton,
  FieldRow,
  TitleBlock
} from "./style";
import type { EditableDebtField, RefinanceDebt } from "./types";

type DebtCardProps = {
  debt: RefinanceDebt;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onChange: (key: EditableDebtField, value: string) => void;
};

export const DebtCard = ({
  debt,
  index,
  expanded,
  onToggle,
  onChange
}: DebtCardProps) => {
  const accountNumberError = getAccountNumberError(debt.bankgiroNumber);
  const paymentReferenceError = getPaymentReferenceError(
    debt.paymentReference ?? ""
  );

  return (
    <Card $verified={debt.valid}>
      <CardHeader onClick={onToggle}>
        <CardHeaderLeft>
          <Badge $verified={debt.valid}>
            {debt.valid ? <BadgeCheck>✓</BadgeCheck> : <WarningIcon />}
            {debt.valid ? "Verified" : "Not verified"}
          </Badge>
          <TitleBlock>
            <CardTitle>
              {expanded
                ? debt.bankName || "Unknown bank"
                : `${debt.bankName || "Unknown bank"} · ${typeLabel(debt.type)}`}
            </CardTitle>
            {expanded && (
              <CardSubtitle>
                {`${typeLabel(debt.type)} · ${sourceLabel(debt.isFromUc)}`}
              </CardSubtitle>
            )}
          </TitleBlock>
        </CardHeaderLeft>
        <CardHeaderRight>
          <CardAmount>{formatAmount(debt.amount)}</CardAmount>
          <ChevronButton>
            {expanded ? <ChevronUp /> : <ChevronDown />}
          </ChevronButton>
        </CardHeaderRight>
      </CardHeader>

      {expanded && (
        <CardBody>
          <FieldRow>
            <FormText
              name={`bankName-${index}`}
              label="Bank"
              required
              defaultValue={debt.bankName ?? ""}
              onChange={(value: string) => onChange("bankName", value)}
            />
            <FormText
              name={`paymentReference-${index}`}
              label="Payment reference"
              placeholder="OCR or reference"
              defaultValue={debt.paymentReference ?? ""}
              onChange={(value: string) => onChange("paymentReference", value)}
              hasError={!!paymentReferenceError}
              errorMessage={paymentReferenceError}
            />
          </FieldRow>
          <FormText
            name={`bankgiroNumber-${index}`}
            label="Bankgiro"
            required
            defaultValue={debt.bankgiroNumber ?? ""}
            onChange={(value: string) => onChange("bankgiroNumber", value)}
            hasError={!!accountNumberError}
            errorMessage={accountNumberError}
          />
        </CardBody>
      )}
    </Card>
  );
};
